import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { CustomErrorBoundary } from "@frontend/components/error_fallback";
import { AppLayout, type LayoutHandle } from "@frontend/components/layout/AppLayout";
import { RootLayout } from "@frontend/components/layout/RootLayout";
import { authLoader } from "@frontend/utils/auth.loader";
import * as Sentry from "@sentry/react";
import { createBrowserRouter, type RouteObject, redirect } from "react-router";

type NavbarFields = {
	nb_icon?: string;
};

type BaseRouterWithNavbar = RouteObject & NavbarFields;
type ReactRouterWithNavbar = BaseRouterWithNavbar & {
	children?: ReactRouterWithNavbar[];
};

// HydrateFallback component for initial app loading
const HydrateFallback = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			minHeight: "100vh",
			bgcolor: "background.default",
		}}
	>
		<LoadingSpinner size={48} />
	</Box>
);

// react-router `lazy` route property: the router awaits the chunk before
// swapping the page, so `useNavigation().state` is "loading" during the fetch
// (which drives the TopProgressBar) instead of React.lazy's blank-Suspense flash.
const lazyRoute = (importer: () => Promise<{ default: React.ComponentType }>) =>
	async () => {
		const mod = await importer();
		return { Component: mod.default };
	};

const routerObjectWithNavbar: ReactRouterWithNavbar[] = [
	{
		path: "/",
		Component: RootLayout,
		errorElement: <CustomErrorBoundary />,
		hydrateFallbackElement: <HydrateFallback />,
		children: [
			{
				index: true,
				loader: () => redirect("/dashboard"),
			},
			{
				path: "auth/*",
				lazy: lazyRoute(() => import("@frontend/modules/auth/auth.router")),
			},
			// The speaker's link (root ADR-035): one talk, no sign-in, no team, its own chrome.
			{
				path: "s/:token",
				lazy: lazyRoute(() => import("@frontend/modules/speaker/pages/SpeakerLink.page")),
			},
			// A visitor's half of a room at a stand (root ADR-036): no sign-in, one conversation, gone when it ends.
			{
				path: "r/:token",
				lazy: lazyRoute(() => import("@frontend/modules/room-join/pages/RoomJoin.page")),
			},
			// Authenticated routes with AppLayout
			{
				element: <AppLayout />,
				loader: authLoader,
				children: [
					{
						path: "dashboard",
						lazy: lazyRoute(() => import("@frontend/pages/Dashboard.page")),
					},
					{
						path: "billing",
						lazy: lazyRoute(() => import("@frontend/pages/Billing.page")),
					},
					{
						// Outside `events/*` so its handle reaches AppLayout's useMatches.
						path: "events/:eventId/stages/:stageId/desk",
						handle: { bare: true } satisfies LayoutHandle,
						lazy: lazyRoute(() => import("@frontend/modules/stage-sessions/pages/StageDesk.page")),
					},
					{
						// A stand at an event: phone-first, its own chrome. Anyone holding
						// a stand may open it; the stands are listed on the dashboard.
						path: "stands/*",
						handle: { bare: true } satisfies LayoutHandle,
						lazy: lazyRoute(() => import("@frontend/modules/exhibitor/exhibitor.router")),
					},
					{
						// An event's short link (root ADR-040): on to the stand, or the event page.
						path: "e/:code",
						lazy: lazyRoute(() => import("@frontend/modules/events/pages/EventCode.page")),
					},
					{
						// The exhibitor app's first address, before stands were per event.
						path: "x/*",
						loader: () => redirect("/dashboard"),
					},
					{
						path: "events/*",
						lazy: lazyRoute(() => import("@frontend/modules/events/events.router")),
					},
					{
						path: "teams/*",
						lazy: lazyRoute(() => import("@frontend/modules/teams/teams.router")),
					},
					{
						path: "profile",
						lazy: lazyRoute(() => import("@frontend/pages/Profile.page")),
					},
				],
			},
		],
	},
];

// Call this AFTER Sentry.init()
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
  createBrowserRouter,
);

export const router = sentryCreateBrowserRouter(routerObjectWithNavbar);
